import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSeo, { BASE } from "@/components/feature/PageSeo";
import { categories, people } from "@/mocks/people";

const personEventMap: Record<string, string> = {
  "renwu-01": "guitar-boy-chengdu",
  "renwu-02": "potter-homecoming",
  "renwu-03": "county-barber",
  "renwu-04": "catcafe-healing",
  "renwu-05": "shadow-puppet-live",
  "renwu-06": "open-mic-explosion",
};

export default function Renwu() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    return people.filter((p) => {
      const okCat = cat === "all" || p.category === cat;
      const okQ =
        !q.trim() ||
        `${p.name}${p.nick}${p.city}`.toLowerCase().includes(q.trim().toLowerCase());
      return okCat && okQ;
    });
  }, [cat, q]);

  return (
    <>
      <PageSeo
        title="热门主角档案｜吃瓜爆网新晋网红与爆红人物盘点"
        description="吃瓜爆网热门主角档案收录新晋网红与爆红人物，按才艺、手作非遗、职业达人、萌宠治愈等栏目持续更新，一网打尽当下最受关注的出圈主角。"
        keywords="吃瓜爆网热门主角,新晋网红盘点,爆红人物档案,才艺达人,非遗手作,职业网红"
        path="/renwu"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "热门主角档案",
            url: BASE + "/renwu",
            about: "新晋网红与爆红人物档案",
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "热门主角",
            itemListElement: people.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: p.name,
              url: BASE + "/renwu",
            })),
          },
        ]}
      />
      <div className="min-h-screen bg-background-50">
        <Header />
        <main>
          <section className="border-b border-background-200 bg-background-100">
            <div className="container-x py-10 md:py-14">
              <span className="eyebrow">
                <i className="ri-user-star-line" aria-hidden="true" />
                热门主角档案
              </span>
              <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
                新晋网红与爆红人物盘点
              </h1>
              <p className="mt-5 max-w-2xl text-[15px] leading-7 text-foreground-700 md:text-base">
                这里收录着最近最受关注的走红主角——从才艺少年到手艺匠人，从县城理发师到治愈系店员。每个人背后，都是一条可以被读懂的真实出圈之路。
              </p>
            </div>
            <Breadcrumb items={[{ label: "热门主角档案" }]} />
          </section>

          <section className="container-x py-10 md:py-14">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="栏目筛选">
                {categories.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    role="tab"
                    aria-selected={cat === c.key}
                    onClick={() => setCat(c.key)}
                    className={`rounded-full px-4 py-1.5 font-label text-sm transition-colors cursor-pointer ${
                      cat === c.key
                        ? "bg-primary-500 text-background-50"
                        : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="relative sm:w-64">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true" />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="搜索主角或城市"
                  aria-label="搜索主角或城市"
                  className="w-full rounded-full border border-background-300 bg-background-50 py-2 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/20"
                />
              </div>
            </div>

            {list.length === 0 ? (
              <div className="mt-16 flex flex-col items-center text-center">
                <i className="ri-user-unfollow-line text-4xl text-foreground-400" aria-hidden="true" />
                <p className="mt-4 font-label text-foreground-700">没有找到匹配的主角，换个栏目或关键词试试吧。</p>
                <button
                  type="button"
                  onClick={() => {
                    setCat("all");
                    setQ("");
                  }}
                  className="btn-ghost mt-6 cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            ) : (
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((p) => {
                  const evtId = personEventMap[p.id];
                  return (
                    <article key={p.id} className="card group flex flex-col overflow-hidden p-0 transition-colors hover:border-primary-300">
                      <div className="relative aspect-[16/10] overflow-hidden bg-background-200">
                        <img
                          src={p.img}
                          alt={`新晋网红 ${p.name}（${p.nick}）插画档案`}
                          title={`${p.name} 热门主角`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background-50/90 px-2.5 py-1 font-label text-xs font-medium text-primary-700">
                          <i className="ri-fire-line" aria-hidden="true" />
                          {p.heat}
                        </span>
                        <span className="absolute bottom-3 right-3 rounded-full bg-accent-500 px-2.5 py-1 font-label text-xs font-medium text-background-950">
                          {p.categoryLabel}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <div className="flex items-baseline justify-between gap-3">
                          <h2 className="font-heading text-xl font-bold text-foreground-950">{p.name}</h2>
                          <span className="inline-flex items-center gap-0.5 font-label text-xs text-secondary-700">
                            <i className="ri-arrow-up-line" aria-hidden="true" />
                            {p.trend}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-600">
                          {p.nick} · {p.city}
                        </p>
                        <p className="mt-3 flex-1 text-sm leading-7 text-foreground-700">{p.story}</p>
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {p.tags.map((t) => (
                            <span key={t} className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                              {t}
                            </span>
                          ))}
                        </div>
                        {evtId && (
                          <Link
                            to={`/shijian/${evtId}`}
                            className="mt-5 inline-flex items-center gap-1.5 font-label text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
                          >
                            阅读 TA 的走红记录
                            <i className="ri-arrow-right-line" aria-hidden="true" />
                          </Link>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}